import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Remove } from '@mui/icons-material';
import { serviceService, Service as ApiService } from '../../services/serviceService';

interface PackageServiceItem {
  serviceId: string;
  quantity: number;
}

interface PackageFormData {
  name: string;
  description: string;
  services: PackageServiceItem[];
  discountPercentage: number;
  validityDays: number;
  isActive: boolean;
}

interface PackageFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PackageFormData) => void;
  initialData?: Partial<PackageFormData>;
  mode: 'create' | 'edit';
}

const PackageForm: React.FC<PackageFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  mode,
}) => {
  const [formData, setFormData] = useState<PackageFormData>({
    name: '',
    description: '',
    services: [],
    discountPercentage: 0,
    validityDays: 30,
    isActive: true,
  });

  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [services, setServices] = useState<ApiService[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Load services from API
  const loadServices = async () => {
    try {
      setLoadingServices(true);
      const response = await serviceService.getServices({ isActive: true, limit: 100 });
      setServices(response.services);
    } catch (err) {
      console.error('Failed to load services:', err);
      setServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadServices(); // Refresh services every time dialog opens
    }
  }, [open]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        services: initialData.services || [],
        discountPercentage: initialData.discountPercentage || 0,
        validityDays: initialData.validityDays || 30,
        isActive: initialData.isActive !== undefined ? initialData.isActive : true,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        services: [],
        discountPercentage: 0,
        validityDays: 30,
        isActive: true,
      });
    }
  }, [initialData, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Package name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData.services.length === 0) {
      newErrors.services = 'At least one service is required';
    }

    if (formData.discountPercentage < 0 || formData.discountPercentage > 100) {
      newErrors.discountPercentage = 'Discount must be between 0 and 100';
    }

    if (formData.validityDays < 1) {
      newErrors.validityDays = 'Validity must be at least 1 day';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
      onClose();
    }
  };

  const handleAddService = () => {
    if (selectedServiceId && !formData.services.find(s => s.serviceId === selectedServiceId)) {
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, { serviceId: selectedServiceId, quantity: 1 }]
      }));
      setSelectedServiceId('');
    }
  };

  const handleRemoveService = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(s => s.serviceId !== serviceId)
    }));
  };

  const handleQuantityChange = (serviceId: string, quantity: number) => {
    if (quantity < 1) return;
    setFormData(prev => ({
      ...prev,
      services: prev.services.map(s => 
        s.serviceId === serviceId ? { ...s, quantity } : s
      )
    }));
  };

  const getServiceById = (id: string) => services.find(s => s.id === id);

  const calculateTotalPrice = () => {
    return formData.services.reduce((total, ps) => {
      const service = getServiceById(ps.serviceId);
      return total + (service ? service.price * ps.quantity : 0);
    }, 0);
  };

  const calculateDiscountedPrice = () => {
    const total = calculateTotalPrice();
    return total - (total * formData.discountPercentage / 100);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Create New Package' : 'Edit Package'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Package Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Validity (Days)"
              type="number"
              value={formData.validityDays}
              onChange={(e) => setFormData(prev => ({ ...prev, validityDays: parseInt(e.target.value) || 0 }))}
              error={!!errors.validityDays}
              helperText={errors.validityDays}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              error={!!errors.description}
              helperText={errors.description}
              required
            />
          </Grid>

          {/* Services Selection */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Services
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <FormControl fullWidth>
              <InputLabel>Add Service</InputLabel>
              <Select
                value={selectedServiceId}
                label="Add Service"
                onChange={(e) => setSelectedServiceId(e.target.value)}
                disabled={loadingServices}
              >
                {loadingServices ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading services...
                  </MenuItem>
                ) : (
                  services
                    .filter(service => !formData.services.find(s => s.serviceId === service.id))
                    .map((service) => (
                      <MenuItem key={service.id} value={service.id}>
                        {service.name} - {formatCurrency(service.price)} ({service.duration}min)
                      </MenuItem>
                    ))
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddService}
              disabled={!selectedServiceId}
              sx={{ height: '56px' }}
            >
              Add Service
            </Button>
          </Grid>

          {errors.services && (
            <Grid item xs={12}>
              <Typography color="error" variant="body2">
                {errors.services}
              </Typography>
            </Grid>
          )}

          {/* Selected Services Table */}
          {formData.services.length > 0 && (
            <Grid item xs={12}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.services.map((ps) => {
                      const service = getServiceById(ps.serviceId);
                      if (!service) return null;
                      return (
                        <TableRow key={ps.serviceId}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">{service.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {service.category} • {service.duration}min
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{formatCurrency(service.price)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(ps.serviceId, ps.quantity - 1)}
                                disabled={ps.quantity <= 1}
                              >
                                <Remove />
                              </IconButton>
                              <Typography>{ps.quantity}</Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(ps.serviceId, ps.quantity + 1)}
                              >
                                <Add />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>{formatCurrency(service.price * ps.quantity)}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveService(ps.serviceId)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {/* Pricing */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Pricing
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Discount Percentage"
              type="number"
              value={formData.discountPercentage}
              onChange={(e) => setFormData(prev => ({ ...prev, discountPercentage: parseFloat(e.target.value) || 0 }))}
              error={!!errors.discountPercentage}
              helperText={errors.discountPercentage}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.isActive ? 'active' : 'inactive'}
                label="Status"
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'active' }))}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Pricing Summary */}
          {formData.services.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  Pricing Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Price:
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(calculateTotalPrice())}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Package Price:
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(calculateDiscountedPrice())}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      You Save:
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {formatCurrency(calculateTotalPrice() - calculateDiscountedPrice())}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Discount:
                    </Typography>
                    <Typography variant="h6">
                      {formData.discountPercentage}%
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {mode === 'create' ? 'Create Package' : 'Update Package'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PackageForm;